import { Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loading } from "@/components/ui/Loading";
import {
  getStoredToken,
  homePathForRole,
  useAuthUser,
  validateSession,
  type UserRole,
} from "@/lib/auth";

export function RequireAuth({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getStoredToken();
      if (!token) {
        if (!cancelled) {
          setValid(false);
          setReady(true);
        }
        return;
      }
      const ok = await validateSession();
      if (!cancelled) {
        setValid(ok);
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return <Loading />;
  if (!valid) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireRole({
  role,
  children,
}: {
  role: UserRole;
  children: ReactNode;
}) {
  const user = useAuthUser();
  if (user?.role !== role)
    return <Navigate to={homePathForRole(user?.role ?? "central")} replace />;
  return <>{children}</>;
}

export function CentralRoute({ children }: { children: ReactNode }) {
  return <RequireRole role="central">{children}</RequireRole>;
}

export function AdminRoute({ children }: { children: ReactNode }) {
  return (
    <RequireRole role="admin">
      <div data-portal="admin">{children}</div>
    </RequireRole>
  );
}

function Fallback() {
  return <Loading />;
}

export function wrap(element: ReactNode) {
  return <Suspense fallback={<Fallback />}>{element}</Suspense>;
}

export function centralPage(element: ReactNode) {
  return wrap(<CentralRoute>{element}</CentralRoute>);
}

export function adminPage(element: ReactNode) {
  return wrap(<AdminRoute>{element}</AdminRoute>);
}
